<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="mt-5 filter-container"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="6" />
      <vs-col vs-w="2">
        <date-picker
          class="px-2"
          type="date"
          format="DD-MMM-YYYY"
          value-type="format"
          placeholder="Start Date"
          v-model="filters.start_date"
          :custom-disabled-date="disabledStartDate"
        />
      </vs-col>
      <vs-col vs-w="2">
        <date-picker
          class="px-2 "
          type="date"
          format="DD-MMM-YYYY"
          value-type="format"
          placeholder="End Date"
          v-model="filters.end_date"
          :custom-disabled-date="disabledEndDate"
        />
      </vs-col>
      <vs-col
        vs-w="1"
        class="flex flex-col justify-center"
      >
        <vs-button
          type="border"
          color="primary"
          class="self-center font-semibold"
          style="min-width: 100px;"
          @click="resetFilters"
        >
          Reset
        </vs-button>
      </vs-col>
    </vs-row>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        hide-search
        :page-size-start="false"
        class="mt-6"
        @fetch="fetchFuelPrices()"
      >
        <template #start>
          <div
            class="flex items-center justify-center"
          >
            <vs-button
              class="button w-max font-semibold"
              color="primary"
              @click="startAdd"
              v-show="isUserCanAccessMenu(permission.name, 'add')"
            >
              Add New Fuel Price
            </vs-button>
            <vs-button
              class="button w-max font-semibold ml-5"
              color="secondary"
              @click="isDownload = true"
            >
              Export
            </vs-button>
          </div>
        </template>
      </table-top>
      <data-table
        class="mt-6"
        :table-header="fuelPriceHeaders"
        :table-data="fuelPriceData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        body-height="75vh"
        :loading="isBusy"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row">
            <template v-if="row.isEditing">
              <div
                :class="`mx-1 self-center flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click="isProcessing ? null : saveEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-check-circle.svg')"
                  alt="save"
                >
              </div>
              <div
                :class="`mx-1 self-center flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click="isProcessing ? null : cancelEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-close-circle.svg')"
                  alt="delete"
                >
              </div>
            </template>
            <template v-else>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="mx-1 self-center flex items-center justify-center icon-transparent-bg"
                @click="startEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-edit.svg')">
              </div>
            </template>
          </div>
        </template>
        <template #date="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <date-picker
                  v-model="row.editForm.date.value"
                  style="min-width: 200px;"
                  type="date"
                  value-type="YYYY-MM-DD"
                  format="DD-MMM-YYYY"
                  :class="{ 'is-not-valid': !row.editForm.date.isValid }"
                  placeholder="Date"
                  :disabled="isProcessing"
                  :max="moment().format('YYYY-MM-DD')"
                />
              </div>
              <span
                v-if="!row.editForm.date.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.date ? moment(row.date).format('DD MMM YYYY') : '-' }}
          </template>
        </template>
        <template #price_idr_per_litre="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <autonumeric
                  :disabled="isProcessing"
                  v-model="row.editForm.price_idr_per_litre.value"
                  :class="{ 'is-not-valid': !row.editForm.price_idr_per_litre.isValid }"
                  class="w-56"
                  placeholder="Enter price IDR per litre"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.price_idr_per_litre.value"
                class="text-danger text-sm absolute -bottom-5 left-0"
              >* Required</span>
            </div>
          </template>
          <template v-else>
            {{ `Rp ${row.price_idr_per_litre ? formatRupiah(row.price_idr_per_litre) : '-'}` }}
          </template>
        </template>
      </data-table>
      <table-bottom
        class="mt-6"
        @fetch="fetchFuelPrices"
      />
    </div>
    <pop-up
      :active.sync="popUp.isShown"
      :is-warning="true"
      title="Duplicate Date Found"
      left-button="Cancel"
      right-button="Replace"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, tempId: null, tempName: '', duplicateData: null, isAdding: false, currentRowId: null }"
      @leftButton="popUp = { isShown: false, tempId: null, tempName: '', duplicateData: null, isAdding: false, currentRowId: null }"
      @rightButton="replaceDuplicateFuelPrice()"
    >
      <p class="text-center mt-1">
        A fuel price already exists for "{{ popUp.tempName }}".
      </p>
      <p class="text-center">
        Do you want to replace it with the new price?
      </p>
    </pop-up>
  </div>
</template>

<script>

import moment from "moment";
import {mapGetters} from "vuex";

import Autonumeric from "@/components/autonumeric";
import DataTable from "@/components/dataTable";
import DatePicker from "@/components/datePicker";
import PopUp from "@/components/popUp";
import TableBottom from "@/components/tableBottom";
import TableTop from "@/components/tableTop";
import { PAGE_TITLE } from "@/fixtures/Constants";
import downloadUtils from "@/utils/downloadUtils";

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    TableTop,
    TableBottom,
    DataTable,
    DatePicker,
    Autonumeric,
    PopUp
  },
  data() {
    return {
      fuelPriceHeaders: [
        { key: 'no', label: 'No', sortable: false, width: '20px' },
        { key: 'date', label: 'Date', sortable: true },
        { key: 'price_idr_per_litre', label: 'Price IDR per litre', sortable: true }
      ],
      fuelPriceData: [],
      globalNumericOptions: {
        digitGroupSeparator: ',',
        decimalCharacter: '.',
        decimalPlaces: 2,
        minimumValue: '0',
        allowDecimalPadding: true
      },
      filters: {
        sort_by: '',
        sort_dir: '',
        start_date: '',
        end_date: ''
      },
      isAdding: false,
      isDownload: false,
      popUp: {
        isShown: false,
        tempId: null,
        tempName: '',
        duplicateData: null,
        isAdding: false,
        currentRowId: null
      },
      isBusy: false,
      isProcessing: false
    }
  },
  computed: {
    ...mapGetters({
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      fuelPrices: 'FuelPrice/fuelPrices',
      fuelPricesDownload: 'FuelPrice/fuelPricesDownload'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    }
  },
  mounted() {
    this.fetchFuelPrices()
  },
  watch: {
    fuelPrices: {
      handler(val) {
        if (val) {
          this.fuelPriceData = val.map(item => ({
            ...item,
            isEditing: false,
            editForm: null
          }))
        }
      }
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.fuelPriceHeaders.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.fuelPriceHeaders.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.fuelPriceHeaders.splice(index, 1);
        }
      }
    },
    filters: {
      deep: true,
      handler() {
        this.fetchFuelPrices()
      }
    },
    fuelPricesDownload: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `ListFuelPrices.xlsx`)
          this.$store.dispatch('FuelPrice/resetFuelPricesDownload');
        }
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadFuelPrices();
        }
      }
    }
  },
  methods: {
    disabledStartDate(date) {
      if (this.filters.end_date) {
        try {
          const endDate = moment(this.filters.end_date, 'DD-MMM-YYYY').startOf('day')
          return date > endDate.toDate()
        } catch (e) {
          return false
        }
      }
      return false
    },
    disabledEndDate(date) {
      if (this.filters.start_date) {
        try {
          const startDate = moment(this.filters.start_date, 'DD-MMM-YYYY').startOf('day')
          return date < startDate.toDate()
        } catch (e) {
          return false
        }
      }
      return false
    },
    async fetchFuelPrices() {
      try {
        this.isBusy = true
        this.isAdding = false;
        const apiFilters = Object.keys(this.filters)
          .filter(key => !!this.filters[key])
          .reduce((item, key) => {
            item[key] = this.filters[key]
            return item
          }, {})
        await this.$store.dispatch(
          'FuelPrice/fetchFuelPrices',
          {
            ...apiFilters
          }
        )
      } catch (e) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch fuel price list, please try again')
      } finally {
        this.isBusy = false
      }
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy
      this.filters.sort_dir = sortDir

      this.fetchFuelPrices()
    },
    startAdd() {
      // Cancel any existing edit
      if (this.isAdding || this.fuelPriceData.some(row => row.isEditing)) {
        this.showFailedNotification(this, 'Only one action can be active at a time. \n' +
          'Please save your changes first.')
        return
      }

      // Create new row
      const newRow = {
        id: `new-${Date.now()}`, // Temporary ID for new row
        isEditing: true,
        active: true,
        editForm: {
          date: { value: '', isValid: true },
          price_idr_per_litre: { value: '', isValid: true }
        }
      }

      this.isAdding = true
      this.fuelPriceData.unshift(newRow)
    },
    startEdit(row) {
      // Cancel any existing edit or add
      if (this.isAdding || this.fuelPriceData.some(r => r.isEditing)) {
        this.showFailedNotification(
          this,
          'Only one action can be active at a time. \n' + 'Please save your changes first.',
          'Error'
        )
        return
      }

      const index = this.fuelPriceData.findIndex(item => item.id === row.id)
      if (index !== -1) {
        this.$set(this.fuelPriceData[index], 'isEditing', true)
        this.$set(this.fuelPriceData[index], 'editForm', {
          date: { value: row.date, isValid: true },
          price_idr_per_litre: { value: row.price_idr_per_litre, isValid: true }
        })
      }
    },
    cancelAllEdits() {
      this.fuelPriceData.forEach(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false)
          this.$set(row, 'editForm', null)
        }
      })
      this.isAdding = false
    },
    cancelEdit(row) {
      if (this.isAdding) {
        const index = this.fuelPriceData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.fuelPriceData.splice(index, 1)
        }
      } else {
        const index = this.fuelPriceData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          const originalData = this.fuelPrices.find(item => item.id === row.id)
          if (originalData) {
            this.$set(this.fuelPriceData[index], 'isEditing', false)
            this.$set(this.fuelPriceData[index], 'editForm', null)
            this.$set(this.fuelPriceData[index], 'date', originalData.date)
            this.$set(this.fuelPriceData[index], 'price_idr_per_litre', originalData.price_idr_per_litre)
          }
        }
      }
      this.isAdding = false
    },
    async saveEdit(row) {
      if (this.validateEditForm(row.editForm)) {
        this.showFailedNotification(
          this,
          'Some fields is empty, please fill it first',
          'Failed'
        )
        return
      }
      try {
        const formData = {
          date: row.editForm.date.value,
          price_idr_per_litre: row.editForm.price_idr_per_litre.value
        }

        const dateFilter = moment(formData.date).format('DD-MMM-YYYY')
        let sameDayFuelPrices
        try {
          sameDayFuelPrices = await this.$store.dispatch('FuelPrice/fetchFuelPricesForDuplicateCheck', {
            start_date: dateFilter,
            end_date: dateFilter
          })
        } catch (e) {
          this.showFailedNotification(
            this,
            e?.message || 'Failed to verify duplicate date, please try again',
            'Error'
          )
          return
        }
        const existingRecord = sameDayFuelPrices.find(item =>
          this.isAdding || item.id !== row.id
        )
        
        if (existingRecord) {
          this.popUp = {
            isShown: true,
            tempId: existingRecord.id,
            tempName: moment(formData.date).format('DD MMM YYYY'),
            duplicateData: formData,
            isAdding: this.isAdding,
            currentRowId: this.isAdding ? null : row.id
          }
          return
        }
        this.isProcessing = true
        if (this.isAdding) {
          await this.$store.dispatch('FuelPrice/addFuelPrice', { form: formData })
          this.showSuccessNotification(this, 'Fuel Price successfully added')
        } else {
          await this.$store.dispatch('FuelPrice/editFuelPrice', { id: row.id, form: formData })
          this.showSuccessNotification(this, 'Fuel Price successfully updated')
        }
        this.cancelAllEdits()
        await this.fetchFuelPrices()
        this.isProcessing = false
      } catch ({ error }) {
        this.isProcessing = false
        this.showFailedNotification(
          this,
          error?.errors || 'Failed to save changes, please try again',
          'Error'
        )
      }
    },
    validateEditForm(form) {
      Object.keys(form).forEach(key => {
        if (form[key].hasOwnProperty('isValid')) {
          form[key].isValid = !!form[key].value
        }
      })

      return Object.keys(form).some(key =>
        form[key].hasOwnProperty('isValid')
          ? !form[key].isValid
          : false
      )
    },
    formatRupiah(value) {
      return new Intl.NumberFormat('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      }).format(value)
    },
    async replaceDuplicateFuelPrice() {
      try {
        const duplicateData = this.popUp.duplicateData
        await this.$store.dispatch('FuelPrice/editFuelPrice', {
          id: this.popUp.tempId,
          form: { ...duplicateData, update_on_duplicate: true }
        })
        this.cancelAllEdits()
        await this.fetchFuelPrices()
        this.showSuccessNotification(this, 'Fuel price replaced successfully')
        this.popUp = { isShown: false, tempId: null, tempName: '', duplicateData: null, isAdding: false, currentRowId: null }
      } catch ({ error }) {
        this.showFailedNotification(this, error?.errors || 'Failed to replace fuel price, please try again', 'Error')
      }
    },
    resetFilters() {
      this.filters = {
        sort_by: '',
        sort_dir: '',
        start_date: '',
        end_date: ''
      };

      this.fetchFuelPrices()
    },
    async downloadFuelPrices() {
      try {
        this.isDownload = true
        this.isBusy = true
        const payload = {
          sort_by: this.filters.sort_by || 'date',
          sort_dir: this.filters.sort_dir || 'desc'
        }

        if (this.filters.start_date) {
          payload.start_date = this.filters.start_date
        }
        if (this.filters.end_date) {
          payload.end_date = this.filters.end_date
        }

        await this.$store.dispatch('FuelPrice/downloadFuelPrices', payload, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          resposnseType: 'blob'
        });
        this.isDownload = false;
      } catch (error) {
        this.isDownload = false
        this.showFailedNotification(this, 'Failed to download Fuel Price, please try again')
      } finally {
        this.isDownload = false
        this.isBusy = false
      }
    }
  },
  beforeCreate() {
    this.$store.dispatch('Table/resetConfig')
  }
}
</script>

<style lang="scss" scoped>
/deep/.vs-textarea {
  min-height: 20px;
  height: calc(1rem * 10 / 3);
  resize: none;
}

.filter-container {
  @apply p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  background-color: #493493;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.is-not-valid {
  /deep/.vs-con-input,
  /deep/.mx-input-wrapper,
  /deep/.bg-accent-multiselect {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}

.button-l-plan {
  @apply flex;
  @apply items-center;
  @apply justify-center;
  padding: 0.15rem 0.25rem;
  border-radius: 5px;
}

.btn-download {
  background-color: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
}

.badge-status {
  background-color: #4CAF5033;
  color: #4CAF50;
  text-align: center;
  padding: 5px 0;
  border-radius: 5px;

  &.inactive {
    background-color: rgba(100, 100, 100, 0.2);
    color: #646464
  }
}
</style>